const ALLOWED_TAGS = new Set(['SPAN', 'STRONG', 'BR', 'EM']);
const ALLOWED_CLASSES = new Set([
  'home-v2__hero-eyebrow-light',
  'home-v2__hero-eyebrow-bold',
  'home-v2__hero-title-line',
  'home-v2__hero-title-line--accent',
  'home-v2__hero-subtitle-highlight',
]);

const HERO_MARKUP_PATTERN = /<(span|strong|br|em)\b/i;

export function escapeHeroHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function containsHeroMarkup(text: string): boolean {
  return HERO_MARKUP_PATTERN.test(text);
}

/** Sanitiza HTML permitido para textos administrables del hero (B.2). */
export function sanitizeHeroHtml(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }

  if (!containsHeroMarkup(trimmed)) {
    return escapeHeroHtml(trimmed);
  }

  if (typeof DOMParser === 'undefined') {
    return escapeHeroHtml(trimmed);
  }

  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  return serializeAllowedHeroNodes(doc.body).trim();
}

function serializeAllowedHeroNodes(parent: ParentNode): string {
  let html = '';

  parent.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      html += escapeHeroHtml(node.textContent ?? '');
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as HTMLElement;
    const tag = element.tagName;

    if (!ALLOWED_TAGS.has(tag)) {
      html += serializeAllowedHeroNodes(element);
      return;
    }

    if (tag === 'BR') {
      html += '<br>';
      return;
    }

    const classes = [...element.classList].filter((className) =>
      ALLOWED_CLASSES.has(className)
    );
    const classAttr = classes.length ? ` class="${classes.join(' ')}"` : '';
    const tagName = tag.toLowerCase();

    html += `<${tagName}${classAttr}>${serializeAllowedHeroNodes(element)}</${tagName}>`;
  });

  return html;
}

export function plainTextToHeroHtml(
  raw: string,
  field: 'eyebrow' | 'title' | 'subtitle'
): string {
  const text = escapeHeroHtml(raw.trim());
  if (!text) {
    return '';
  }

  switch (field) {
    case 'eyebrow':
      return `<span class="home-v2__hero-eyebrow-light">${text}</span>`;
    case 'title':
      return `<span class="home-v2__hero-title-line">${text}</span>`;
    default:
      return text;
  }
}

export function prepareHeroFieldHtml(
  raw: string,
  field: 'eyebrow' | 'title' | 'subtitle'
): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }

  if (containsHeroMarkup(trimmed)) {
    return sanitizeHeroHtml(trimmed);
  }

  return plainTextToHeroHtml(trimmed, field);
}
